import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  CreateIncidentPayload,
  Incident,
  IncidentListItem,
  UpdateIncidentPayload,
} from '../../shared/models/incident.models';
import { PageParams } from '../../shared/models/pagination.models';
import { Person, PersonsResponse } from '../../shared/models/person.models';

export interface IncidentsResponse {
  incidents: IncidentListItem[];
  total: number;
  params: PageParams;
}

export interface IncidentFilterParams {
  pageSize?: number;
  pageIndex?: number;
  filter?: string;
  programId?: string;
  categoryId?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortDirection?: string;
  /** A, B, C, D or Completed. */
  stage?: string;
}

@Injectable({ providedIn: 'root' })
export class IncidentService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/api`;

  // ── Incidents ─────────────────────────────────────────────────────────────

  getIncidents(params: IncidentFilterParams = {}) {
    let httpParams = new HttpParams()
      .set('pageSize', params.pageSize ?? 100)
      .set('pageIndex', params.pageIndex ?? 0)
      .set('filter', params.filter ?? '');
    if (params.programId) httpParams = httpParams.set('programId', params.programId);
    if (params.categoryId) httpParams = httpParams.set('categoryId', params.categoryId);
    if (params.stage) httpParams = httpParams.set('stage', params.stage);
    if (params.dateFrom) httpParams = httpParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) httpParams = httpParams.set('dateTo', params.dateTo);
    if (params.sortBy) httpParams = httpParams.set('sortBy', params.sortBy);
    if (params.sortDirection) httpParams = httpParams.set('sortDirection', params.sortDirection);
    return this.http.get<IncidentsResponse>(`${this.base}/incidents`, { params: httpParams });
  }

  getIncidentById(id: string) {
    return this.http.get<Incident>(`${this.base}/incidents/${id}`);
  }

  createIncident(payload: CreateIncidentPayload) {
    return this.http.post<Incident>(`${this.base}/incidents`, payload);
  }

  updateIncident(payload: UpdateIncidentPayload) {
    return this.http.put<Incident>(`${this.base}/incidents`, payload);
  }

  deleteIncident(id: string) {
    return this.http.delete<void>(`${this.base}/incidents/${id}`);
  }

  // ── Persons ───────────────────────────────────────────────────────────────

  getPersons(
    filter?: string,
    pageSize = 50,
    personTypeCode?: string,
    excludePersonTypeCode?: string,
    pageIndex = 0,
  ) {
    let httpParams = new HttpParams().set('pageSize', pageSize).set('pageIndex', pageIndex);
    if (filter) httpParams = httpParams.set('filter', filter);
    if (personTypeCode) httpParams = httpParams.set('personTypeCode', personTypeCode);
    if (excludePersonTypeCode)
      httpParams = httpParams.set('excludePersonTypeCode', excludePersonTypeCode);
    return this.http.get<PersonsResponse>(`${this.base}/persons`, { params: httpParams });
  }

  createPerson(payload: { firstName: string; surnames: string }) {
    return this.http.post<Person>(`${this.base}/persons`, payload);
  }
}
